import DirectusSDK from '@directus/sdk-js';
import LanguageCode from '@/locale/language-code';
import CMSModel from '@/visa-application/models/CMSModel';
import { getLocalStorage } from '@/shared/utils/utils';
import _ from 'lodash';
import { ALL_COUNTRY_CODE, ALL_ISSUER_CODE, ISSUER_REDIRECT_TOKEN } from '@/constants';
import { translationStore } from '@/shared/config/store/translation-store';

export default class DirectusService {
  public tokensDirectus: Map<string, string> = new Map<string, string>();

  constructor(private client: DirectusSDK) {}

  public getCollection<T extends CMSModel>(
    model: new () => T,
    collection: string,
    fields: string[],
    filter?: {},
    lang?: LanguageCode,
    sort?: string
  ): Promise<T[]> {
    return new Promise<T[]>((resolve, reject) => {
      const statusFilter = {
        status: {
          eq: 'published'
        }
      };
      filter = { ...filter, ...statusFilter };
      const params = {
        fields,
        ...{ filter },
        ...(lang !== undefined && { lang }),
        ...(sort !== undefined && { sort })
      };

      this.client
        .getItems(collection, params)
        .then(response => {
          const data: T[] = [];

          response.data.forEach(responseItem => {
            const dataItem: T = new model();
            dataItem.map(responseItem);
            data.push(dataItem);
          });
          // replace all Detail Code
          if (getLocalStorage('issuer') && getLocalStorage('lang')) {
            this.replaceAllDetailCode(data);
          }
          resolve(data);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public async replaceAllDetailCode(collectionData) {
    for (const collectionDataKey in collectionData) {
      if (collectionData.hasOwnProperty(collectionDataKey)) {
        if (collectionData[collectionDataKey] && typeof collectionData[collectionDataKey] === 'object') {
          this.replaceAllDetailCode(collectionData[collectionDataKey]);
        } else if (collectionData[collectionDataKey] && typeof collectionData[collectionDataKey] === 'string') {
          collectionData[collectionDataKey] = await this.replaceDetailCodes(
            collectionData[collectionDataKey],
            getLocalStorage('issuer'),
            getLocalStorage('lang')
          );

          // long_desc field hyperlink color configuration
          // long_desc字段超链接颜色配置
          if (
            collectionData[collectionDataKey] &&
            (collectionDataKey === 'long_desc' || collectionDataKey === 'description') &&
            collectionData[collectionDataKey].includes('<a')
          ) {
            collectionData[collectionDataKey] = collectionData[collectionDataKey].replaceAll('<a', '<a class="long_desc"');
          }
        }
      }
    }
  }

  /**
   * Replaces all occurences of all details in the text. Detail codes and their values are
   * automatically fetched from the SDK.
   * @param text The text that contains detail codes.
   * @param applicationCenter The current application center.
   * @param lang The lang of the detail codes.
   */
  public async replaceDetailCodes(text: string, applicationCenter: string, lang): Promise<string> {
    const detailCodes: string[] = this.getDetailCodes(text);

    if (detailCodes === undefined || detailCodes.length < 1) {
      return text;
    }

    let replacedText = text;
    let tokenText = '';

    detailCodes.forEach(detailCode => {
      if (detailCode === ISSUER_REDIRECT_TOKEN && getLocalStorage('residenceCountryCode') !== ALL_COUNTRY_CODE) {
        tokenText = getLocalStorage('residenceCountryCode') + '/' + getLocalStorage('issuer');
      } else if (detailCode === 'auth' && getLocalStorage('residenceCountryCode') !== ALL_COUNTRY_CODE && getLocalStorage('issuer')) {
        const port = window.location.port ? ':' + window.location.port : '';
        // Check if the center is enabled for login
        tokenText = `//${window.location.hostname}${port}/register/${getLocalStorage('residenceCountryCode')}/${getLocalStorage('issuer')}`;
        // 跳转到登录页面
      } else if (detailCode === 'login') {
        const port = window.location.port ? ':' + window.location.port : '';
        tokenText = `//${window.location.hostname}${port}/oauth2/authorization/oidc`;
      } else {
        tokenText = _.get(_.find(this.tokensDirectus, { detail_code: { code: detailCode } }), 'translation.0.value', '') || '';
        while ((tokenText && tokenText.indexOf('<p>') !== -1) || tokenText.indexOf('</p>') !== -1) {
          tokenText = tokenText.replace('<p>', '').replace('</p>', '');
        }
      }
      replacedText = this.replaceAllOccurencesOfADetailCode(replacedText, detailCode, tokenText);
    });
    return replacedText;
  }

  public getDetailCodesFromDirectus(countryCode: string, applicationCenter: string, lang): Promise<any> {
    const fields = ['detail_code.code', 'translation.value', 'tag'];
    const filter = {
      status: {
        eq: 'published'
      }
    };

    return new Promise((resolve, reject) => {
      this.client
        .getItems('application_center_detail', {
          fields,
          filter,
          lang
        })
        .then(result => {
          resolve(this.uniqDetailCodeDatas(result.data, countryCode, applicationCenter));
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public getAllDetailCodesFromDirectus(countryCode: string, applicationCenter: string, lang: LanguageCode): Promise<any> {
    const fields = ['detail_code.code', 'translation.value', 'tag'];
    const filter = {
      status: {
        eq: 'published'
      }
      // 'application_center.code': {
      //   in: [applicationCenter, ALL_ISSUER_CODE]
      // }
    };

    return new Promise((resolve, reject) => {
      this.client
        .getItems('application_center_detail', {
          fields,
          filter,
          lang
        })
        .then(result => {
          resolve(this.uniqDetailCodeDatas(result.data, countryCode, applicationCenter));
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  public uniqDetailCodeDatas(detailCodeDatas: any[], countryCode: string, applicationCenterCode: any): any[] {
    // 去除tag中不符合条件的token
    // The tokens that do not meet the conditions are removed from the TAG
    const list = detailCodeDatas.filter(item => {
      if (
        item.tag.some(tagItem => tagItem === applicationCenterCode.toLowerCase() || tagItem === countryCode || tagItem === ALL_ISSUER_CODE)
      ) {
        return item;
      }
    });
    // 根据不同的层次区分token 并根据detail_cod进行去重
    // Distinguish tokens according to different levels and de-weight according to detail_COD
    const currentIssuerDatas = _.uniqBy(
      list.filter(item => item.tag.some(tagItem => tagItem === applicationCenterCode.toLowerCase())).reverse(),
      'detail_code.code'
    );
    const countryList = _.uniqBy(list.filter(item => item.tag.some(tagItem => tagItem === countryCode)).reverse(), 'detail_code.code');
    const AllList = _.uniqBy(list.filter(item => item.tag.some(tagItem => tagItem === ALL_ISSUER_CODE)).reverse(), 'detail_code.code');
    // 为了层次优先处理 issuer > countryCode > all
    // For hierarchical priority processing
    countryList.forEach(item => {
      if (currentIssuerDatas.every(issuer => issuer.detail_code.code !== item.detail_code.code) || !currentIssuerDatas.length) {
        currentIssuerDatas.push(item);
      }
    });
    AllList.forEach(item => {
      if (currentIssuerDatas.every(issuer => issuer.detail_code.code !== item.detail_code.code) || !currentIssuerDatas.length) {
        currentIssuerDatas.push(item);
      }
    });
    return currentIssuerDatas;
  }

  /**
   * Retrieves all detail codes from a string (detail codes are variables to replace from the CMS)
   * In order to replace detail codes from the CMS
   * @param text The text to parse
   */
  public getDetailCodes(text: string): string[] {
    const detailRegexp: RegExp = new RegExp('{{[\\s]*[\\w]+[\\s]*}}', 'gi');
    let detailCodes = text.match(detailRegexp);

    if (detailCodes && detailCodes.length > 0) {
      detailCodes = detailCodes.map(detailCode => detailCode.replace(/{{[\s]*|[\s]*}}/g, ''));
    }
    return Array.from(new Set(detailCodes));
  }

  /**
   * Replace all occurences of a detail code in the text.
   * @param text The text that contains detail codes to be replaced.
   * @param detailCode The detail code to replace. This is the code without the curly brackets.
   * @param replaceBy The text to replace the detail code occurences by.
   */
  public replaceAllOccurencesOfADetailCode(text: string, detailCode: string, replaceBy: string): string {
    const regexp: RegExp = new RegExp('{{[\\s]*' + detailCode + '[\\s]*}}', 'gi');

    if (replaceBy) {
      return text.replace(regexp, replaceBy);
    }
    return text.replace(regexp, '');
  }
}
